/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import logoGrayMark from "../../../static/logo-gray-mark.png";
import acceptPortfolioIllustration from "../../../static/illustration--accept-portfolio.svg";
import { useAnswer } from "../../../util/form";
import { useAppStore } from "../../../store/store";
import { useToast } from "../../../contexts/ToastContext";

export default function AcceptPortfolio({
  advanceScreen,
  previousScreen,
  validation,
  updateValidation,
  screen,
}) {
  const { showError } = useToast();
  const { user, form, token } = useAppStore();

  // We don't need to set an answer in this screen as it would overwrite the portfolio selection
  // from screen 3.14, but we keep the hook for consistency
  useAnswer(screen.id);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    updateAnswer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateAnswer() {
    // Don't set an answer here - this would overwrite the portfolio selection from screen 3.14
    // Just update validation to allow continuing
    updateValidation(true);
  }

  // Function to handle portfolio acceptance and API call
  const handleAcceptPortfolio = async () => {
    setIsSubmitting(true);
    try {
      const portfolioId =
        form.selectedPortfolioId ??
        user?.portfolioId ??
        0;

      console.log("Submitting portfolio acceptance with ID:", portfolioId);

      // Prepare the API config with authentication
      const apiConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
      };

      // Find maximize benefits answers from both RTQ and Goals paths
      const maximizeBenefitsRtq = form.questions.find(
        (q) => q.questionId === "3.16",
      );
      const maximizeBenefitsGoals = form.questions.find(
        (q) => q.questionId === "2.14",
      );
      const maximizeBenefits =
        maximizeBenefitsRtq?.answerKey === "yes" ||
        maximizeBenefitsGoals?.answerKey === "yes";

      const outsideAssets = form.questions.find(
        (q) => q.questionId === "outside-assets",
      );

      const formattedOutsideAssets = [
        ...(outsideAssets?.answerKey?.outsideAssets || []),
      ].map((asset) => ({
        outsideAssetTypeId: Number(asset.outsideAssetId),
        name: asset.name,
        value: Number(asset.value),
      }));

      // Prepare the payload with the selected portfolio ID and maximize benefits answer
      const payload = {
        selectedPortfolioId: portfolioId,
        maximizeBenefits,
        formAnswers: form.questions, // Include all form answers for completeness
        outsideAssets: formattedOutsideAssets,
      };

      const baseUrl = window.API_ENDPOINT;
      const response = await axios.post(
        `${baseUrl}/api/v1/Users/complete`,
        payload,
        apiConfig,
      );

      console.log("Portfolio acceptance response:", response.data);

      // Proceed to the next screen
      advanceScreen();
    } catch (error) {
      console.error("Error submitting portfolio acceptance:", error);
      // Still advance even if there's an error to not block the user
      // advanceScreen();
      showError(
        "There was an error processing your request. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      className="w-full h-full"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
    >
      <div className="px-6 py-16 md:px-16 md:py-20">
        <div className="mx-auto max-w-[820px]">
          <div className="max-w-3xl mx-auto mb-16 text-center md:mb-20">
            <h1 className="text-3xl md:text-4xl">
              We’re ready to start optimizing the investments in your retirement
              account!
            </h1>
            <img
              className="block w-full h-auto max-w-xl mx-auto mt-8 md:mt-12"
              src={acceptPortfolioIllustration}
              alt=""
            />
          </div>
          <div className="grid grid-cols-1 divide-gray-300 max-md:divide-y md:grid-cols-2 md:divide-x">
            <div className="max-md:pb-8 md:pr-8 lg:pr-12">
              <p>
                Artesys will send instructions to Voya to allocate your account
                to this actively managed portfolio.
              </p>
              <p>
                You will be able to login to your{" "}
                <strong>{user.recordKeeper}</strong> account in 1-2 business
                days and see the new investments you’re in.
              </p>
              <p>
                Artesys charges <strong>{user.quarterlyFeePercent}%</strong> of
                your retirement account on a quarterly basis in arrears. This
                fee will be deducted directly from your retirement account and
                not affect your take-home pay.
              </p>
            </div>
            <div className="max-md:pt-8 md:pl-8 lg:pl-12">
              <div className="flex items-center">
                <img
                  className="block h-[2.4679rem] w-11"
                  src={logoGrayMark}
                  alt=""
                />
                <h3 className="ml-2 text-lg font-bold text-teal-700">
                  Remember with Artesys:
                </h3>
              </div>
              <div className="mt-4 md:mt-7">
                <ul className="mb-3 list md:mb-5">
                  <li>There is no sign-up cost.</li>
                  <li>There is no contractual obligation.</li>
                  <li>
                    You can change your portfolio or terminate the service at
                    anytime for no additional cost.
                  </li>
                </ul>
                <p>
                  Contact us with any questions.
                  <br />
                  <a
                    className="font-normal"
                    href="mailto:artesys@artesysonline.com"
                    target="_blank"
                    rel="noreferrer"
                  >
                    artesys@artesysonline.com
                  </a>
                  <br />
                  <a
                    className="font-normal"
                    href="tel:+13147835000"
                    target="_blank"
                    rel="noreferrer"
                  >
                    (314) 783-5000
                  </a>
                </p>
              </div>
            </div>
          </div>
          <div className="flex justify-center gap-3 mt-12 md:mt-16 md:gap-5">
            <button className="btn btn--secondary" onClick={previousScreen}>
              Back
            </button>
            <button
              className="btn btn--primary"
              disabled={Boolean(validation) === false || isSubmitting}
              onClick={handleAcceptPortfolio}
            >
              {isSubmitting ? "Processing..." : "Accept & Complete"}
            </button>
          </div>
          <div className="mt-6 italic text-center md:mt-8">
            <p className="text-xs">
              By clicking ‘Accept and Complete’, you acknowledge receipt of the
              ADV by electronic delivery and that you have read, understand and
              agree to the Client Service Agreement, Terms of Use, and our
              Privacy Policy. Section 11 of the Terms of Use contains a
              mandatory arbitration clause.
            </p>
            <p className="text-xs">
              Please check your email for a copy of our agreement and your
              recommendation information.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
